import { useEffect, useState, useContext, useCallback } from "react";
import { Grid } from "@mui/material";
import styles from "./styles.module.css";
import Loader from "../../utils/Loading/index";
import UserService from "../../../services/UserService";
import { ROUTES, STORAGE_KEYS } from "../../../const";
import { useNavigate } from "react-router-dom";
import ShopContext from "../../../context/ShopContext";
import ShopService from "../../../services/ShopService";
import toast from "react-hot-toast";
import * as AppSettings from "helpers/AppSettingsHelper";
import UserHelper from "helpers/UserHelper";
import validityExpired from "../../utils/validityExpired";

function ShopSelectionPage() {
  const navigate = useNavigate();
  const { setSelectedShop, setShopId } = useContext(ShopContext);

  const [isLoading, setIsLoading] = useState(false);
  const [shops, setShops] = useState([]);

  const selectShopHandler = useCallback(
    async (shop) => {
      setIsLoading(true);

      try {
        const oldShopId = await AppSettings.getSetting(STORAGE_KEYS.SHOP_ID);

        if (oldShopId && oldShopId !== shop.id) {
          console.warn("Shop changed! Clearing all IndexedDB data...");
          await AppSettings.clearAllIndexedDB();
        }

        localStorage.setItem(STORAGE_KEYS.SHOP_ID, shop.id);
        localStorage.setItem(STORAGE_KEYS.SHOP_NAME, shop.shop_name);

        await AppSettings.saveSetting(STORAGE_KEYS.SHOP_ID, shop.id);
        const fullProfile = await ShopService.loadShopProfile(false);

        setSelectedShop(fullProfile);
        setShopId(shop.id);

        if (validityExpired(fullProfile?.subscription_end_date)) {
          navigate(ROUTES.SUBSCRIPTION_EXPIRED);
        } else {
          navigate(ROUTES.COUNTER);
        }
      } catch (err) {
        console.error("ShopSelectionPage.selectShopHandler error", err);
        toast.error("Failed to load shop");
      } finally {
        setIsLoading(false);
      }
    },
    [setSelectedShop, setShopId, navigate],
  );

  const getUserShops = useCallback(async () => {
    setIsLoading(true);
    try {
      const resp = await UserService.getUserShops();
      const shopsArray = Array.isArray(resp) ? resp : (resp?.data ?? []);

      if (shopsArray.length === 1) {
        selectShopHandler(shopsArray[0]);
        return;
      }
      setShops(shopsArray);
    } catch (error) {
      toast.log("Error: ", error);
    } finally {
      setIsLoading(false);
    }
  }, [selectShopHandler]);

  const getShopProfile = async () => {
    setIsLoading(true);
    try {
      return await ShopService.loadShopProfile(false);
    } catch (err) {
      console.error("loadShopProfile error", err);
      toast.error("Failed to load shop profile");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      if (!UserHelper.isLoggedIn()) {
        navigate(ROUTES.LOGIN);
        return;
      }

      if (localStorage.getItem(STORAGE_KEYS.SHOP_ID)) {
        try {
          setIsLoading(true);
          const profile = await getShopProfile();
          if (profile) {
            setSelectedShop(profile);
            setShopId(profile.id);
            if (validityExpired(profile?.subscription_end_date)) {
              navigate(ROUTES.SUBSCRIPTION_EXPIRED);
            } else {
              navigate(ROUTES.COUNTER);
            }
            return;
          }
        } catch (err) {
          console.error("loadShopProfile error", err);
        } finally {
          setIsLoading(false);
        }
      }

      await getUserShops();
    })();
  }, [getUserShops, navigate, setSelectedShop, setShopId]);

  return (
    <>
      <Loader isOpen={isLoading} />
      <Grid container rowSpacing={6} className={styles.textArea}>
        <Grid item xs={12}>
          <Grid container rowSpacing={1}>
            <Grid item xs={12} display="flex" justifyContent={"center"}>
              <span className={styles.head1}>
                Select <span className={styles.head2}>Shop</span>
              </span>
            </Grid>
            <Grid item xs={12} className={styles.text}>
              <span>Please select your shop to continue billing.</span>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} style={{ paddingTop: "24px" }}>
          <Grid container className={styles.selectWrapper}>
            {shops.map((shop) => (
              <Grid
                key={shop?.id}
                item
                xs={12}
                textAlign={"center"}
                onClick={() => selectShopHandler(shop)}
                className={styles.selectContainer}
              >
                <div className={styles.shopName}>{shop?.shop_name}</div>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>
    </>
  );
}

export default ShopSelectionPage;
